import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Card, CardBody, CardTitle, Badge, Button, Table, Modal, ModalHeader, ModalBody, Spinner, FormGroup, Label, Input } from "reactstrap";
import { DataTable, Breadcrumb } from "../../../components/Common";
import { useTenant } from "../../../hooks/useTenant";
import moment from "moment";
import { LANDING_PLAN_CATALOG, getLandingPlanByCode } from "../../LandingPage/planCatalog";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import BillingService from "../../../services/billing_service";
import { toast } from "react-toastify";

const MERCADO_PAGO_SDK_URL = "https://sdk.mercadopago.com/js/v2";

const loadMercadoPagoSdk = () => new Promise((resolve, reject) => {
    if (window.MercadoPago) {
        resolve(window.MercadoPago);
        return;
    }

    const existingScript = document.querySelector(`script[src="${MERCADO_PAGO_SDK_URL}"]`);
    if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window.MercadoPago));
        existingScript.addEventListener("error", () => reject(new Error("Não foi possível carregar o SDK do Mercado Pago.")));
        return;
    }

    const script = document.createElement("script");
    script.src = MERCADO_PAGO_SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.MercadoPago);
    script.onerror = () => reject(new Error("Não foi possível carregar o SDK do Mercado Pago."));
    document.body.appendChild(script);
});

const TenantBillingPage = () => {
    document.title = "Financeiro | NeoApp - Sistema de Gestão";

    const { idTenant, user } = useTenant();
    const [tenantData, setTenantData] = useState(null);
    const [loadingTenant, setLoadingTenant] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    
    // Configurações do método de pagamento
    const [selectedMethod, setSelectedMethod] = useState("pix");
    const [showCardForm, setShowCardForm] = useState(false);
    const [savingMethod, setSavingMethod] = useState(false);
    const [brickLoading, setBrickLoading] = useState(false);
    const [brickError, setBrickError] = useState("");
    const brickControllerRef = useRef(null);

    // Modal de Pix
    const [pixModalOpen, setPixModalOpen] = useState(false);
    const [currentPixCode, setCurrentPixCode] = useState("");
    const [currentPixQrBase64, setCurrentPixQrBase64] = useState("");

    const fetchTenantData = useCallback(async () => {
        if (!idTenant) return;
        try {
            setLoadingTenant(true);
            const docRef = doc(db, "tenants", idTenant);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                const data = snap.data();
                setTenantData(data);
                setSelectedMethod(data.billingPaymentMethod || "pix");
            }
        } catch (error) {
            console.error("Erro ao buscar dados do tenant:", error);
            toast.error("Erro ao carregar dados da assinatura.");
        } finally {
            setLoadingTenant(false);
        }
    }, [idTenant]);

    const fetchInvoices = useCallback(async () => {
        try {
            setLoadingInvoices(true);
            const result = await BillingService.getTenantBillingInvoices();
            if (result.success) {
                setInvoices(result.invoices || []);
            }
        } catch (error) {
            console.error("Erro ao buscar faturas:", error);
            toast.error("Erro ao carregar histórico de faturas.");
        } finally {
            setLoadingInvoices(false);
        }
    }, []);

    useEffect(() => {
        fetchTenantData();
        fetchInvoices();
    }, [fetchTenantData, fetchInvoices]);

    // Montar Mercado Pago Payment Brick para Cartão Recorrente
    useEffect(() => {
        if (selectedMethod !== "recurrent" || !showCardForm || !idTenant || !tenantData) {
            return undefined;
        }

        const publicKey = process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY || "";
        if (!publicKey) {
            setBrickError("Chave pública do Mercado Pago não configurada.");
            return undefined;
        }

        let cancelled = false;

        const mountBrick = async () => {
            setBrickLoading(true);
            setBrickError("");

            try {
                await loadMercadoPagoSdk();

                if (cancelled) return;

                if (brickControllerRef.current?.unmount) {
                    await brickControllerRef.current.unmount();
                }

                const mp = new window.MercadoPago(publicKey, { locale: "pt-BR" });
                const bricksBuilder = mp.bricks();

                const container = document.getElementById("billing-payment-brick");
                if (container) {
                    container.innerHTML = "";
                }

                const planCode = tenantData.checkoutPlanCode || "neo";
                const resolvedPlan = getLandingPlanByCode(planCode) || LANDING_PLAN_CATALOG.find(p => p.code === "neo");
                const priceAmount = resolvedPlan ? resolvedPlan.price : 490;

                brickControllerRef.current = await bricksBuilder.create("payment", "billing-payment-brick", {
                    initialization: {
                        amount: Number(priceAmount),
                        payer: {
                            email: user?.email || tenantData.email || "",
                            entityType: "individual"
                        },
                    },
                    customization: {
                        visual: {
                            style: {
                                theme: "dark",
                            },
                        },
                        paymentMethods: {
                            creditCard: "all",
                            debitCard: "all",
                            maxInstallments: 1
                        },
                    },
                    callbacks: {
                        onReady: () => {
                            if (!cancelled) setBrickLoading(false);
                        },
                        onSubmit: async (brickState) => {
                            setSavingMethod(true);
                            try {
                                const token = brickState?.formData?.token || brickState?.token;
                                const paymentMethodId = brickState?.formData?.payment_method_id || brickState?.payment_method_id;

                                const res = await BillingService.registerTenantCard(token, paymentMethodId);
                                if (res.success) {
                                    toast.success("Cartão de crédito cadastrado com sucesso!");
                                    setShowCardForm(false);
                                    await fetchTenantData();
                                }
                            } catch (err) {
                                console.error("Erro ao cadastrar cartão:", err);
                                toast.error(err.message || "Erro ao cadastrar cartão.");
                            } finally {
                                setSavingMethod(false);
                            }
                        },
                        onError: (error) => {
                            console.error("Erro no Card Brick:", error);
                            if (!cancelled) {
                                setBrickLoading(false);
                                setBrickError("Erro ao carregar formulário de cartão.");
                            }
                        }
                    }
                });

            } catch (err) {
                console.error("Erro ao inicializar Card Brick:", err);
                if (!cancelled) {
                    setBrickError("Não foi possível carregar o formulário de cartão.");
                    setBrickLoading(false);
                }
            }
        };

        mountBrick();

        return () => {
            cancelled = true;
            if (brickControllerRef.current?.unmount) {
                brickControllerRef.current.unmount().catch(() => {});
            }
        };
    }, [selectedMethod, showCardForm, idTenant, tenantData, user?.email, fetchTenantData]);

    const handleSaveBillingMethod = async () => {
        setSavingMethod(true);
        try {
            const res = await BillingService.updateTenantBillingMethod(selectedMethod);
            if (res.success) {
                toast.success(`Forma de pagamento alterada para ${selectedMethod === "pix" ? "Pix" : "Boleto"} com sucesso!`);
                await fetchTenantData();
            }
        } catch (err) {
            console.error("Erro ao atualizar forma de pagamento:", err);
            toast.error(err.message || "Erro ao atualizar forma de pagamento.");
        } finally {
            setSavingMethod(false);
        }
    };

    const planCode = tenantData?.checkoutPlanCode || "neo";
    const plan = getLandingPlanByCode(planCode) || LANDING_PLAN_CATALOG.find(p => p.code === "neo");
    const isTrial = tenantData?.billingStatus === "trial_started" || plan?.isTrial;

    // Colunas do DataTable
    const columns = [
        {
            key: "id",
            label: "ID da Fatura",
            render: (row) => <span className="text-body fw-bold">{row.id}</span>
        },
        {
            key: "date",
            label: "Data",
            render: (row) => row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY") : "—"
        },
        {
            key: "planTitle",
            label: "Plano / Referência",
            render: (row) => `Assinatura ${row.planTitle || "NeoApp"}`
        },
        {
            key: "amount",
            label: "Valor",
            render: (row) => `R$ ${Number(row.amount || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
        },
        {
            key: "status",
            label: "Status",
            render: (row) => {
                const status = row.paymentStatus || "unknown";
                if (status === "approved") {
                    return <Badge color="success" className="font-size-12 px-2 py-1 text-white">Pago</Badge>;
                }
                if (status === "pending") {
                    return <Badge color="warning" className="font-size-12 px-2 py-1 text-white">Pendente</Badge>;
                }
                if (status === "rejected" || status === "cancelled") {
                    return <Badge color="danger" className="font-size-12 px-2 py-1 text-white">Cancelado / Recusado</Badge>;
                }
                return <Badge color="secondary" className="font-size-12 px-2 py-1 text-white">{status}</Badge>;
            }
        },
        {
            key: "actions",
            label: "Ação",
            render: (row) => {
                const isPending = row.paymentStatus === "pending";
                const isPix = String(row.paymentMethodId).toUpperCase() === "PIX";
                const isBoleto = String(row.paymentMethodId).toUpperCase() === "BOLETO" || row.paymentMethodId?.toLowerCase().includes("boleto");

                return (
                    <div className="d-flex gap-2">
                        {isBoleto && row.ticket_url && (
                            <a href={row.ticket_url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm rounded-0">
                                <i className="mdi mdi-barcode font-size-16 me-1"></i> Pagar Boleto
                            </a>
                        )}
                        {isPix && isPending && (row.qr_code || row.qr_code_base64) && (
                            <Button
                                type="button"
                                color="info"
                                size="sm"
                                className="rounded-0 waves-effect waves-light shadow-none"
                                onClick={() => {
                                    setCurrentPixCode(row.qr_code || "");
                                    setCurrentPixQrBase64(row.qr_code_base64 || "");
                                    setPixModalOpen(true);
                                }}
                            >
                                <i className="mdi mdi-qrcode-scan font-size-16 me-1"></i> Pagar Pix
                            </Button>
                        )}
                        {!isPending && row.paymentStatus === "approved" && (
                            <span className="text-success font-size-12 fw-medium d-flex align-items-center">
                                <i className="mdi mdi-check-circle me-1 font-size-15"></i> Pago
                            </span>
                        )}
                    </div>
                );
            }
        }
    ];

    if (loadingTenant) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Carregando dados da assinatura...</p>
            </div>
        );
    }

    const currentMethodLabel = 
        tenantData?.billingPaymentMethod === "recurrent" ? "Cartão de Crédito Recorrente" :
        tenantData?.billingPaymentMethod === "pix" ? "Pix" :
        tenantData?.billingPaymentMethod === "boleto" ? "Boleto" : "Não definido";

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Financeiro da Assinatura" />
            </div>

            <Row>
                {/* Resumo do Plano Atual e Forma de Pagamento */}
                <Col xl={5}>
                    <Card className="mb-4">
                        <CardBody>
                            <CardTitle className="mb-4">Meu Plano Atual</CardTitle>

                            <div className="d-flex align-items-center mb-4">
                                <div className="avatar-lg me-3 flex-shrink-0" style={{ width: "50px", height: "50px" }}>
                                    {tenantData?.logoUrl ? (
                                        <img src={tenantData.logoUrl} alt="Logo" className="img-thumbnail rounded-circle w-100 h-100 object-fit-cover" />
                                    ) : (
                                        <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-20 shadow-sm border border-light d-flex align-items-center justify-content-center h-100 w-100">
                                            {tenantData?.name ? tenantData.name.charAt(0).toUpperCase() : "N"}
                                        </span>
                                    )}
                                </div>
                                <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <h4 className="font-size-16 mb-0 fw-bold">{plan?.title || "Plano NEO"}</h4>
                                        <Badge color={isTrial ? "warning" : "success"} className="font-size-11 px-2 py-1">
                                            {isTrial ? "Período de Teste" : "Ativo"}
                                        </Badge>
                                    </div>
                                    <p className="text-muted font-size-13 mb-0" style={{ lineHeight: "1.4" }}>
                                        {plan?.headline || "Camada premium com automação, IA e expansão"}
                                    </p>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <Table className="table-nowrap mb-0">
                                    <tbody>
                                        <tr>
                                            <th scope="row" className="border-0">Valor Mensal :</th>
                                            <td className="border-0">R$ {plan?.price ? `${plan.price},00` : "0,00"}</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Próximo Vencimento :</th>
                                            <td>
                                                {tenantData?.nextBillingDate
                                                    ? moment(tenantData.nextBillingDate).format("DD/MM/YYYY")
                                                    : tenantData?.trialEndsAt
                                                        ? moment(tenantData.trialEndsAt).format("DD/MM/YYYY")
                                                        : "A calcular"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Forma Atual :</th>
                                            <td>
                                                <span className="fw-semibold text-primary">{currentMethodLabel}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <CardTitle className="mb-4">Alterar Forma de Pagamento</CardTitle>

                            <FormGroup className="mb-4">
                                <Label className="fw-medium text-dark font-size-14 mb-3">Escolha como deseja pagar a assinatura:</Label>
                                <div className="d-flex flex-column gap-3">
                                    <Label className="d-flex align-items-center gap-3 p-3 border rounded cursor-pointer mb-0">
                                        <Input
                                            type="radio"
                                            name="paymentMethod"
                                            value="pix"
                                            checked={selectedMethod === "pix"}
                                            onChange={() => {
                                                setSelectedMethod("pix");
                                                setShowCardForm(false);
                                            }}
                                            className="m-0"
                                        />
                                        <div>
                                            <strong className="d-block font-size-14 text-dark"><i className="mdi mdi-qrcode-scan text-info me-1"></i> Pix</strong>
                                            <span className="text-muted font-size-12">Chave copia e cola gerada mensalmente e enviada por e-mail.</span>
                                        </div>
                                    </Label>

                                    <Label className="d-flex align-items-center gap-3 p-3 border rounded cursor-pointer mb-0">
                                        <Input
                                            type="radio"
                                            name="paymentMethod"
                                            value="boleto"
                                            checked={selectedMethod === "boleto"}
                                            onChange={() => {
                                                setSelectedMethod("boleto");
                                                setShowCardForm(false);
                                            }}
                                            className="m-0"
                                        />
                                        <div>
                                            <strong className="d-block font-size-14 text-dark"><i className="mdi mdi-barcode text-warning me-1"></i> Boleto</strong>
                                            <span className="text-muted font-size-12">Boleto bancário emitido mensalmente e enviado por e-mail.</span>
                                        </div>
                                    </Label>

                                    <Label className="d-flex align-items-center gap-3 p-3 border rounded cursor-pointer mb-0">
                                        <Input
                                            type="radio"
                                            name="paymentMethod"
                                            value="recurrent"
                                            checked={selectedMethod === "recurrent"}
                                            onChange={() => {
                                                setSelectedMethod("recurrent");
                                                if (tenantData?.billingPaymentMethod !== "recurrent") {
                                                    setShowCardForm(true);
                                                }
                                            }}
                                            className="m-0"
                                        />
                                        <div>
                                            <strong className="d-block font-size-14 text-dark"><i className="mdi mdi-credit-card-outline text-success me-1"></i> Cartão de Crédito Recorrente</strong>
                                            <span className="text-muted font-size-12">Cobrança mensal automatizada no cartão de crédito cadastrado.</span>
                                        </div>
                                    </Label>
                                </div>
                            </FormGroup>

                            {selectedMethod === "recurrent" && (
                                <div className="mt-3">
                                    {tenantData?.cardDetails && !showCardForm ? (
                                        <div className="p-3 bg-light rounded border mb-3">
                                            <p className="mb-2 font-size-13 text-dark fw-medium">Cartão de Crédito Cadastrado:</p>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>
                                                    <i className="mdi mdi-credit-card text-primary me-2 font-size-18"></i>
                                                    <strong className="text-capitalize">{tenantData.cardDetails.brand}</strong> final <strong>{tenantData.cardDetails.lastFour}</strong>
                                                    <small className="text-muted ms-2">({tenantData.cardDetails.expiry})</small>
                                                </span>
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    outline
                                                    className="rounded-0"
                                                    onClick={() => setShowCardForm(true)}
                                                >
                                                    Alterar Cartão
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-light rounded border mb-3">
                                            <CardTitle className="font-size-14 mb-3">Formulário do Cartão de Crédito</CardTitle>
                                            {brickError && <div className="alert alert-danger">{brickError}</div>}
                                            <div id="billing-payment-brick">
                                                {brickLoading && (
                                                    <div className="text-center py-4">
                                                        <Spinner color="primary" />
                                                        <p className="mb-0 mt-3 text-muted">Carregando formulário seguro Mercado Pago...</p>
                                                    </div>
                                                )}
                                            </div>
                                            {showCardForm && tenantData?.cardDetails && (
                                                <Button
                                                    color="link"
                                                    className="text-muted font-size-12 w-100 text-center"
                                                    onClick={() => setShowCardForm(false)}
                                                >
                                                    Cancelar alteração
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedMethod !== "recurrent" && selectedMethod !== tenantData?.billingPaymentMethod && (
                                <div className="mt-4">
                                    <Button
                                        color="primary"
                                        className="w-100 rounded-0"
                                        disabled={savingMethod}
                                        onClick={handleSaveBillingMethod}
                                    >
                                        {savingMethod ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Salvando...
                                            </>
                                        ) : (
                                            `Confirmar Alteração para ${selectedMethod === "pix" ? "Pix" : "Boleto"}`
                                        )}
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                {/* Histórico de Faturas */}
                <Col xl={7}>
                    <Card>
                        <CardBody className="p-0">
                            <div className="p-4 border-bottom d-flex align-items-center justify-content-between">
                                <CardTitle className="mb-0">Histórico de Faturas</CardTitle>
                            </div>

                            {loadingInvoices ? (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="mt-3 text-muted">Carregando faturas...</p>
                                </div>
                            ) : (
                                <DataTable 
                                    columns={columns}
                                    data={invoices}
                                    keyField="id"
                                    emptyMessage="Nenhuma fatura encontrada"
                                    emptySubMessage="Seu histórico de pagamentos aparecerá aqui."
                                    noCard={true}
                                />
                            )}

                            {/* Informação sobre Nota Fiscal */}
                            <div className="m-4 p-4 border rounded bg-light">
                                <h5 className="font-size-14 mb-2">
                                    <i className="mdi mdi-information-outline text-primary me-2 font-size-16"></i>Emissão de Nota Fiscal
                                </h5>
                                <p className="text-muted mb-0 font-size-13" style={{ lineHeight: "1.5" }}>
                                    As notas fiscais de serviço são emitidas automaticamente pela NeoApp após a compensação do pagamento.
                                    Uma cópia em PDF e XML é enviada diretamente para o e-mail cadastrado do proprietário da conta ({tenantData?.ownerEmail || tenantData?.email || "não configurado"}).
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Modal Pix copia e cola / QR */}
            <Modal isOpen={pixModalOpen} toggle={() => setPixModalOpen(false)} centered>
                <ModalHeader toggle={() => setPixModalOpen(false)}>Pagamento via Pix</ModalHeader>
                <ModalBody className="text-center py-4">
                    {currentPixQrBase64 && (
                        <div className="mb-4">
                            <p className="fw-semibold font-size-14 text-dark mb-3">Escaneie o QR Code abaixo para pagar:</p>
                            <div style={{ background: "white", padding: "16px", borderRadius: "16px", display: "inline-block", border: "1px solid #ddd" }}>
                                <img
                                    src={`data:image/png;base64,${currentPixQrBase64}`}
                                    alt="QR Code Pix"
                                    style={{ width: "200px", maxWidth: "100%", display: "block" }}
                                />
                            </div>
                        </div>
                    )}
                    {currentPixCode && (
                        <div>
                            <p className="fw-semibold mb-2 font-size-14 text-dark">Chave Pix Copia e Cola:</p>
                            <div className="p-3 bg-light rounded text-break font-size-12 mb-3" style={{ border: "1px solid #eee", maxHeight: "100px", overflowY: "auto", userSelect: "all" }}>
                                {currentPixCode}
                            </div>
                            <Button
                                color="primary"
                                className="w-100 rounded-0"
                                onClick={() => {
                                    navigator.clipboard.writeText(currentPixCode);
                                    toast.success("Chave Pix copiada!");
                                }}
                            >
                                Copiar Chave Pix
                            </Button>
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default TenantBillingPage;
